import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "@/utils";

/**
 * Tooltip — Radix tooltip with portal so it renders above modals/overlays.
 * Provider wraps the whole app (see main.tsx) with delayDuration=700.
 * Usage:
 *   <Tooltip content="Help text"><button>?</button></Tooltip>
 *
 * Or composable:
 *   <TooltipRoot><TooltipTrigger>…</TooltipTrigger><TooltipContent>…</TooltipContent></TooltipRoot>
 */

export const TooltipProvider = TooltipPrimitive.Provider;
export const TooltipRoot = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;

export function TooltipContent({
  className,
  sideOffset = 6,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        sideOffset={sideOffset}
        className={cn(
          "z-[var(--z-tooltip)] px-2 py-1 rounded-[var(--radius-sm)]",
          "bg-[var(--foreground)] text-[var(--card)] text-xs leading-snug",
          "shadow-[var(--shadow-md)] max-w-[240px]",
          "data-[state=delayed-open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=delayed-open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=delayed-open]:zoom-in-95",
          "data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1",
          "data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1",
          className
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className="fill-[var(--foreground)]" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

/**
 * Simple one-liner wrapper — mirrors the old Tooltip API so existing
 * <Tooltip content="…"><child /></Tooltip> usage keeps working.
 */
export default function Tooltip({
  content,
  children,
  side = "top",
  className,
}: {
  content: React.ReactNode;
  children: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  className?: string;
}) {
  return (
    <TooltipRoot>
      <TooltipTrigger asChild>
        <span className="inline-flex">{children}</span>
      </TooltipTrigger>
      <TooltipContent side={side} className={className}>
        {content}
      </TooltipContent>
    </TooltipRoot>
  );
}
